// 공지 배너 컴포넌트 (P0-3) — AppLayout 상단에 표시
import { useState } from 'react'
import { X, Info, AlertTriangle, AlertOctagon, ChevronDown, ChevronUp } from 'lucide-react'
import { useAnnouncements, type Announcement } from '@/hooks/useAnnouncements'

const ICON_MAP = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
}

const STYLE_MAP = {
  info: 'bg-blue-50 border-blue-200 text-blue-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  critical: 'bg-red-50 border-red-300 text-red-900',
}

const ICON_COLOR_MAP = {
  info: 'text-blue-500',
  warning: 'text-amber-500',
  critical: 'text-red-500',
}

function SingleBanner({ ann, onDismiss }: { ann: Announcement; onDismiss: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = ICON_MAP[ann.severity] || Info
  const longBody = ann.body.length > 100

  return (
    <div className={`border-b px-4 py-2.5 ${STYLE_MAP[ann.severity] || STYLE_MAP.info}`}>
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        <Icon size={16} className={`shrink-0 mt-0.5 ${ICON_COLOR_MAP[ann.severity] || ICON_COLOR_MAP.info}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{ann.title}</p>
          {(expanded || !longBody) && (
            <p className="text-xs mt-1 leading-relaxed whitespace-pre-wrap">{ann.body}</p>
          )}
          {longBody && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[11px] mt-1 inline-flex items-center gap-0.5 opacity-75 hover:opacity-100"
            >
              {expanded ? '접기' : '자세히'}
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 p-1 rounded-full hover:bg-black/5"
          title="공지 닫기"
        >
          <X size={14} className="opacity-60" />
        </button>
      </div>
    </div>
  )
}

export default function AnnouncementBanner() {
  const { items, dismiss } = useAnnouncements()

  if (items.length === 0) return null

  // critical 먼저, 그 다음 warning, info 순으로 정렬
  const priority = { critical: 0, warning: 1, info: 2 } as const
  const sorted = [...items].sort(
    (a, b) => (priority[a.severity] ?? 9) - (priority[b.severity] ?? 9)
  )

  return (
    <div className="sticky top-0 z-30">
      {sorted.slice(0, 3).map((ann) => (
        <SingleBanner key={ann.id} ann={ann} onDismiss={() => dismiss(ann.id)} />
      ))}
    </div>
  )
}
